/**
 * Análises (D522): the client-side queue in front of `/v1/analytics/*`. The API lets one operator have at most two heavy
 * reads in flight (a third is a 429 `ANALYTICS_BUSY`), and a dashboard has more tiles than that, so every BI call goes through
 * ONE queue per page: at most `limit` run at once, the rest wait their turn, and a call the server still answers with "busy"
 * is tried again after the `Retry-After` it sent (a few times, then the screen says so in words and offers a retry).
 *
 * Nothing is remembered here: a queue holds tasks, never answers (the BI answers are `no-store`; saved reports keep the query).
 */
import { type Result } from '@vello/core';
import { type ApiError } from '@vello/api-client';
import { biErrorKind } from './bi-errors.js';

export interface BiQueueOptions {
  /** Calls in flight at once (the server's per-user limit is 2). */
  readonly limit?: number;
  /** Tries again after "busy" this many times before giving up. */
  readonly busyRetries?: number;
  /** How long to wait before a retry (default: the server's `Retry-After`, else 1 s, never more than 5 s). */
  readonly wait?: (ms: number) => Promise<void>;
}

export interface BiQueue {
  /** Run `task` when a slot is free. Resolves `undefined` (without running) when `stale()` is true by the time its turn comes. */
  run<T>(task: () => Promise<Result<T, ApiError>>, stale?: () => boolean): Promise<Result<T, ApiError> | undefined>;
  readonly inFlight: () => number;
  readonly waiting: () => number;
}

const defaultWait = (ms: number): Promise<void> => new Promise((resolve) => globalThis.setTimeout(resolve, ms));

export function createBiQueue(options: BiQueueOptions = {}): BiQueue {
  const limit = Math.max(1, options.limit ?? 2);
  const retries = options.busyRetries ?? 2;
  const wait = options.wait ?? defaultWait;
  let active = 0;
  const pending: (() => void)[] = [];

  const release = (): void => {
    active -= 1;
    const next = pending.shift();
    if (next !== undefined) next();
  };
  const acquire = (): Promise<void> => {
    if (active < limit) {
      active += 1;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      pending.push(() => {
        active += 1;
        resolve();
      });
    });
  };

  return {
    inFlight: () => active,
    waiting: () => pending.length,
    async run<T>(task: () => Promise<Result<T, ApiError>>, stale: () => boolean = () => false): Promise<Result<T, ApiError> | undefined> {
      for (let attempt = 0; ; attempt++) {
        await acquire();
        if (stale()) {
          release();
          return undefined;
        }
        let result: Result<T, ApiError>;
        try {
          result = await task();
        } finally {
          release();
        }
        if (result.ok || biErrorKind(result.error) !== 'busy' || attempt >= retries) return result;
        const seconds = result.error.retryAfterSeconds ?? 1;
        await wait(Math.min(5, Math.max(0, seconds)) * 1000);
        if (stale()) return undefined;
      }
    },
  };
}

/** The one queue the Análises screens share (a screen change must not start more than two reads at once). */
export const sharedBiQueue: BiQueue = createBiQueue();
